import { Controller, useFormContext } from "react-hook-form";
import { Box, Typography as T } from "@mui/material";
import debounce from "debounce";

import { InputNumber, Label, LabelError, RequiredStar } from "components/atoms";
import { Car } from "helpers/global-types";
import { useStoreActions } from "store/store";

type Props = {
  labelText: string;
  labelErrorText: string;
  required?: boolean;
  name: string;
  selectedCar: Car | null;
  labelVariant?: "h5md" | "inputTitle";
};

const InputNumberBox = ({
  labelText,
  labelErrorText,
  required,
  name,
  selectedCar,
  labelVariant = "h5md",
}: Props) => {
  const { control } = useFormContext();
  const setNumberOfPassengers = useStoreActions((a) => a.setNumberOfPassengers);
  const updateCartThunk = useStoreActions((a) => a.updateCartThunk);

  const debouncedOnChange = debounce((v: number) => {
    setNumberOfPassengers(v);
    updateCartThunk({ passengers: Number(v) });
  }, 1200);

  return (
    <Controller
      control={control}
      name={name}
      defaultValue="1"
      rules={{
        validate: {
          oneSeat: (v) => {
            return selectedCar?.oneSeatAllowed
              ? parseFloat(v) === 1 ||
                  "Only one seat allowed for this type of vehicle"
              : true;
          },
          reqularValidation: (v) => {
            return required && !!v;
          },
        },
      }}
      render={({
        field: { onChange, value, ref },
        fieldState: { invalid, error },
      }) => {
        const setValue = (e: number) => {
          onChange(e);
          debouncedOnChange(e);
        };

        return (
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Label
              sx={{
                marginBottom: "6px",
                marginLeft: 0,
              }}
            >
              <T variant={labelVariant}>{labelText}</T>
              {required && <RequiredStar />}
            </Label>
            <InputNumber
              inputRef={ref}
              setValue={setValue}
              value={value}
              error={invalid}
            />
            {invalid && (
              <LabelError labelErrorText={error?.message || labelErrorText} />
            )}
          </Box>
        );
      }}
    />
  );
};

export default InputNumberBox;
